import { useEffect, useState } from 'react'
import {
  groupBy,
  monthlySeries,
  onlySpending,
  rootCategoryOf,
  slicesToCsv,
  throughLens,
  totalOf,
  yearOverYear,
} from '@domain/report'
import type { Lens, Slice } from '@domain/report'
import type { Account, Category, CreditCard, Transaction } from '@domain/finance'
import { addMonths } from '@shared/date/reference-month'
import type { ReferenceMonth } from '@shared/date/reference-month'
import { currentReferenceMonth } from '@shared/date/timezone'
import { absCents } from '@shared/money/cents'
import { formatBRL } from '@shared/money/format'
import { Glass } from '@ui/glass/Glass'
import { Numero as NumeroDeDisplay } from '@ui/glass/Numero'
import { monthLabel } from '@ui/finance/labels'
import { useFinanceRepositories } from '@ui/finance/finance-context'

/**
 * Relatórios. `Docs/05-Plano/Fase 11 - Relatórios.md`.
 *
 * ## A LENTE é declarada, sempre, e no topo
 *
 * *"Todo relatório declara qual está usando, sempre visível."* Ela não é um
 * filtro escondido num menu: é a primeira coisa da tela, porque competência e
 * caixa respondem perguntas diferentes e os dois números são igualmente
 * verdadeiros. Um relatório sem a lente declarada é um número sem pergunta.
 *
 * ## Sem agregado por Cloud Function, e a medição é o motivo
 *
 * A nota da fase prevê `aggregates/{YYYY-MM}` mantido por Function, e diz que
 * ele existe *"pela latência da tela, não por limite de escala"*. O maior mês da
 * base tem **91** transações (decisão 144a): doze meses são ~1.100 documentos em
 * doze consultas paralelas. O agregado seria uma segunda fonte de verdade sobre
 * dinheiro, com uma Function para mantê-la, comprando uma latência que já não
 * existe. Reabre quando um mês passar da casa dos milhares.
 *
 * ## O CSV existe porque *"dado que não sai de um sistema é dado refém"*
 *
 * Ele sai do que está na tela — as mesmas fatias, o mesmo recorte, a mesma
 * lente. Um export que refizesse a consulta poderia divergir do que a pessoa
 * acabou de ler.
 */

const JANELA = 12

type Eixo = 'categoria' | 'instrumento' | 'metodo'

const EIXO_LABELS: Record<Eixo, string> = {
  categoria: 'Categoria',
  instrumento: 'Conta ou cartão',
  metodo: 'Método',
}

const METODO_LABELS: Record<string, string> = {
  pix: 'Pix',
  debit: 'Débito',
  credit: 'Crédito',
  boleto: 'Boleto',
  transfer: 'Transferência',
  cash: 'Dinheiro',
  other: 'Outro',
}

function Barra({ slice, total }: { slice: Slice; total: number }) {
  const pct = total === 0 ? 0 : (slice.cents / total) * 100
  return (
    <li className="border-b border-[var(--border)] py-1 text-sm">
      <div className="flex items-baseline gap-2">
        <span className="flex-1">{slice.label}</span>
        <span className="tabular-nums text-[var(--text-muted)]">
          {`${pct.toFixed(1).replace('.', ',')}%`}
        </span>
        <span className="tabular-nums">{formatBRL(absCents(slice.cents))}</span>
      </div>
      {/* A barra é decoração da linha, e o número está escrito ao lado — quem
          usa leitor de tela não perde nada. */}
      <div aria-hidden="true" className="mt-1 h-1 rounded bg-[var(--border)]">
        <div className="h-1 rounded bg-[var(--text-muted)]" style={{ width: `${String(pct)}%` }} />
      </div>
    </li>
  )
}

export function Relatorios() {
  const { transactionRepository, categoryRepository, accountRepository, creditCardRepository } =
    useFinanceRepositories()
  const [lens, setLens] = useState<Lens>('competence')
  const [eixo, setEixo] = useState<Eixo>('categoria')
  const [dados, setDados] = useState<
    | { status: 'loading' | 'failed' }
    | {
        status: 'loaded'
        porMes: Map<ReferenceMonth, { competence: Transaction[]; cash: Transaction[] }>
        categories: Category[]
        accounts: Account[]
        creditCards: CreditCard[]
      }
  >({ status: 'loading' })

  const fim = currentReferenceMonth()
  const meses: ReferenceMonth[] = Array.from({ length: JANELA }, (_, i) =>
    addMonths(fim, -(JANELA - 1 - i)),
  )

  useEffect(() => {
    let live = true
    // Doze meses em consultas paralelas. Ver o cabeçalho para por que não há
    // agregado.
    void Promise.all([
      Promise.all(
        meses.map(async (month) => ({
          month,
          competence: await transactionRepository.listByCompetence(month),
          cash: await transactionRepository.listByCash(month),
        })),
      ),
      categoryRepository.list(),
      accountRepository.list(),
      creditCardRepository.list(),
    ])
      .then(([porMes, categories, accounts, creditCards]) => {
        if (!live) return
        setDados({
          status: 'loaded',
          porMes: new Map(porMes.map((m) => [m.month, { competence: m.competence, cash: m.cash }])),
          categories,
          accounts,
          creditCards,
        })
      })
      .catch((error: unknown) => {
        console.error('Relatorios: a leitura falhou', error)
        if (live) setDados({ status: 'failed' })
      })
    return () => {
      live = false
    }
    // `meses` é derivado do mês corrente, que não muda enquanto a tela vive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionRepository, categoryRepository, accountRepository, creditCardRepository])

  if (dados.status !== 'loaded') {
    return (
      <section>
        <h1 className="titulo mb-4 text-2xl">Relatórios</h1>
        <Glass level="frost" className="p-5">
          {dados.status === 'loading' ? (
            <p role="status" className="text-sm text-[var(--text-muted)]">
              Carregando doze meses…
            </p>
          ) : (
            <p role="alert" className="text-sm text-[var(--text-muted)]">
              Não foi possível carregar os relatórios agora.
            </p>
          )}
        </Glass>
      </section>
    )
  }

  const porId = new Map(dados.categories.map((c) => [c.id, c]))
  const nomes = new Map(dados.categories.map((c) => [c.id, c.name]))
  const instrumentos = new Map([
    ...dados.accounts.map((a) => [a.id, a.name] as const),
    ...dados.creditCards.map((c) => [c.id, c.name] as const),
  ])

  /** As transações de cada mês, já pela lente e só as saídas. */
  const gastoPorMes = new Map(
    meses.map((month) => {
      const m = dados.porMes.get(month) ?? { competence: [], cash: [] }
      return [month, onlySpending(throughLens(m.competence, m.cash, lens, dados.categories), lens)]
    }),
  )
  const todas = [...gastoPorMes.values()].flat()

  const fatias: Slice[] =
    eixo === 'categoria'
      ? groupBy(
          todas,
          (t) => {
            const id = t.enrichment.categoryId
            return id === null ? null : rootCategoryOf(id, porId)
          },
          (k) => nomes.get(k) ?? 'categoria que não existe mais',
          'Sem categoria',
          lens,
        )
      : eixo === 'instrumento'
        ? groupBy(
            todas,
            (t) => t.source.instrument.id,
            (k) => instrumentos.get(k) ?? 'conta que não existe mais',
            'Sem instrumento',
            lens,
          )
        : groupBy(
            todas,
            (t) => t.source.paymentMethod,
            (k) => METODO_LABELS[k] ?? k,
            'Sem método',
            lens,
          )

  const total = totalOf(fatias)
  const serie = monthlySeries(gastoPorMes, meses, lens)
  const maior = Math.max(...serie.map((p) => p.cents), 1)
  const anoAano = yearOverYear(serie, fim)

  const baixarCsv = () => {
    const csv = slicesToCsv(fatias, EIXO_LABELS[eixo])
    // **O BOM é necessário, e vai escapado.** Sem `\ufeff` no começo, o Excel
    // abre o arquivo em Latin-1 e "Alimentação" vira "AlimentaÃ§Ã£o". Escrito
    // como escape porque o caractere literal é invisível — o `eslint` reprova
    // com razão, e um caractere que ninguém vê no editor é um caractere que
    // alguém apaga sem querer.
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `porquinho-${eixo}-${lens}-${fim}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <section>
      <header className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="titulo text-2xl">Relatórios</h1>
        <button type="button" className="alvo justify-start text-sm underline" onClick={baixarCsv}>
          Baixar CSV
        </button>
      </header>

      {/* **A lente, no topo e sempre visível.** Ver o cabeçalho. */}
      <Glass level="frost" className="mb-4 p-5">
        <fieldset>
          <legend className="text-xs text-[var(--text-muted)]">Lente</legend>
          <div className="mt-1 flex flex-wrap gap-4 text-sm">
            {(['competence', 'cash'] as Lens[]).map((l) => (
              <label key={l} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="lente"
                  checked={lens === l}
                  onChange={() => {
                    setLens(l)
                  }}
                />
                <span>
                  {l === 'competence'
                    ? 'Competência — o que consumimos'
                    : 'Caixa — o que saiu da conta'}
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <p className="mt-2 text-xs text-[var(--text-muted)]">
          {lens === 'competence'
            ? 'A compra no cartão conta no mês da compra; o pagamento da fatura não conta.'
            : 'O pagamento da fatura conta no mês em que saiu; a compra no cartão não conta.'}
        </p>
      </Glass>

      <Glass level="frost" className="mb-4 p-5">
        <h2 className="mb-1 text-xs text-[var(--text-muted)]">
          {`Gasto por mês · ${monthLabel(meses[0] ?? fim)} a ${monthLabel(fim)}`}
        </h2>
        <p className="mb-3">
          <NumeroDeDisplay tamanho="medio">{formatBRL(absCents(total))}</NumeroDeDisplay>
        </p>
        <ul className="text-sm">
          {serie.map((ponto) => (
            <li key={ponto.month} className="flex items-baseline gap-2 py-0.5">
              <span className="w-24 text-[var(--text-muted)]">{monthLabel(ponto.month)}</span>
              <span
                aria-hidden="true"
                className="h-2 rounded bg-[var(--text-muted)]"
                style={{ width: `${String((ponto.cents / maior) * 60)}%` }}
              />
              <span className="tabular-nums">{formatBRL(absCents(ponto.cents))}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-[var(--text-muted)]">
          {/* "O app é honesto sobre não saber": sem o ano anterior, não há
              comparação — e dizer "0%" seria inventar. */}
          {anoAano === null
            ? 'Ainda não há o mesmo mês do ano anterior para comparar.'
            : `Mesmo mês do ano anterior: ${formatBRL(absCents(anoAano.anterior))}.`}
        </p>
      </Glass>

      <Glass level="frost" className="p-5">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h2 className="text-xs text-[var(--text-muted)]">Por</h2>
          {(Object.keys(EIXO_LABELS) as Eixo[]).map((e) => (
            <label key={e} className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                name="eixo"
                checked={eixo === e}
                onChange={() => {
                  setEixo(e)
                }}
              />
              <span>{EIXO_LABELS[e]}</span>
            </label>
          ))}
        </div>

        {fatias.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">
            Nenhum gasto nestes doze meses por esta lente.
          </p>
        ) : (
          <ul aria-label={`Gasto por ${EIXO_LABELS[eixo].toLowerCase()}`}>
            {fatias.map((slice) => (
              <Barra key={slice.key} slice={slice} total={total} />
            ))}
          </ul>
        )}

        <p className="mt-3 text-xs text-[var(--text-muted)]">
          {/* A definição de pronto: o total sempre bate com a lista. Dizê-lo na
              tela é o que permite alguém conferir sem abrir o código. */}
          {`Soma das ${String(fatias.length)} linhas acima: ${formatBRL(absCents(total))} — o mesmo total do gráfico.`}
        </p>
      </Glass>
    </section>
  )
}
